import { useState, useMemo, useRef, useEffect } from 'react';
import { useVisibleStore } from '../hooks/useVisibleStore';
import { type Shift, type Team, useStore } from '../store/useStore';
import { ArrowLeft, Search, AlertTriangle, Plus, X, Clock, Pencil, Trash2, UserPlus, ChevronDown, ChevronRight, ChevronLeft, Calendar } from 'lucide-react';
import { PointIcon } from './icons/PointIcon';
import { AddPointTaskModal } from './AddPointTaskModal';

function fmtHour(d: Date) {
  return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

function fmtDayShort(dateStr: string) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
}

/** Jour d'un créneau, au format triable `AAAA-MM-JJ` (heure locale). */
function dayKey(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ─── Gauge data types ──────────────────────────────────────────────────────

interface GaugeSegment {
  start: Date;
  end: Date;
  shift: Shift;
  team: Team;
  isActive: boolean;
}

interface GaugeGap {
  start: Date;
  end: Date;
  isClickable: boolean;
}

interface GaugeLine {
  segments: GaugeSegment[];
  gaps: GaugeGap[];
}

interface GaugeData {
  pointId: string;
  pointName: string;
  pointIcon: string;
  isStage: boolean;
  isMeal: boolean;
  isCompact: boolean;
  categoryName: string;
  timelineStart: Date;
  timelineEnd: Date;
  activeZones: { start: Date; end: Date; requiredTeam: number; isHard: boolean }[];
  inactiveZones: { start: Date; end: Date }[];
  lines: GaugeLine[];
  maxRequiredTeam: number;
  totalAssigned: number;
  hasNoSlots?: boolean;
}

// ─── Gauge data builder ─────────────────────────────────────────────────────

function buildGaugeData(
  pointId: string,
  pointName: string,
  pointIcon: string,
  isStage: boolean,
  isMeal: boolean,
  isCompact: boolean,
  categoryName: string,
  slots: { start: Date; end: Date; requiredTeam: number; isHard: boolean }[],
  allPointShifts: Shift[],
  teamMap: Map<string, Team>,
): GaugeData | null {
  if (slots.length === 0) {
    // Return a minimal gauge for points without slots
    const now = new Date();
    return {
      pointId,
      pointName,
      pointIcon,
      isStage,
      isMeal,
      isCompact,
      categoryName,
      timelineStart: now,
      timelineEnd: new Date(now.getTime() + 3600000),
      activeZones: [],
      inactiveZones: [{ start: now, end: new Date(now.getTime() + 3600000) }],
      maxRequiredTeam: 1,
      lines: [],
      totalAssigned: 0,
      hasNoSlots: true,
    } as GaugeData;
  }

  // Sort slots chronologically
  const sorted = [...slots].sort((a, b) => a.start.getTime() - b.start.getTime());

  // Timeline range
  const timelineStart = new Date(sorted[0].start);
  const timelineEnd = new Date(sorted[sorted.length - 1].end);

  // Active zones (the slots themselves)
  const activeZones = sorted.map(s => ({
    start: new Date(s.start),
    end: new Date(s.end),
    requiredTeam: s.requiredTeam,
    isHard: s.isHard,
  }));

  // Inactive zones (gaps between slots)
  const inactiveZones: { start: Date; end: Date }[] = [];
  for (let i = 0; i < sorted.length - 1; i++) {
    const gapStart = new Date(sorted[i].end);
    const gapEnd = new Date(sorted[i + 1].start);
    if (gapEnd.getTime() > gapStart.getTime()) {
      inactiveZones.push({ start: gapStart, end: gapEnd });
    }
  }

  const maxRequiredTeam = Math.max(...sorted.map(s => s.requiredTeam), 1);

  // Sort shifts by start time for greedy placement
  const sortedShifts = [...allPointShifts].sort((a, b) =>
    new Date(a.start).getTime() - new Date(b.start).getTime()
  );

  // Greedy scheduling: place each shift on the first line where it fits
  const lines: { segments: GaugeSegment[] }[] = [];

  for (const shift of sortedShifts) {
    const sStart = new Date(shift.start);
    const sEnd = new Date(shift.end);
    const team = teamMap.get(shift.teamId);
    if (!team) continue;

    // Check if this shift overlaps with the timeline at all
    if (sEnd <= timelineStart || sStart >= timelineEnd) continue;

    // Clamp shift to timeline
    const clampedStart = new Date(Math.max(sStart.getTime(), timelineStart.getTime()));
    const clampedEnd = new Date(Math.min(sEnd.getTime(), timelineEnd.getTime()));

    // Determine if the shift falls within an active zone
    const isActive = activeZones.some(zone =>
      clampedStart < zone.end && clampedEnd > zone.start
    );

    // Find first line where this shift doesn't overlap
    let placed = false;
    for (const line of lines) {
      const overlaps = line.segments.some(seg =>
        clampedStart < seg.end && clampedEnd > seg.start
      );
      if (!overlaps) {
        line.segments.push({
          start: clampedStart,
          end: clampedEnd,
          shift,
          team,
          isActive,
        });
        placed = true;
        break;
      }
    }

    if (!placed) {
      lines.push({
        segments: [{
          start: clampedStart,
          end: clampedEnd,
          shift,
          team,
          isActive,
        }],
      });
    }
  }

  // Ensure we have at least maxRequiredTeam lines
  while (lines.length < maxRequiredTeam) {
    lines.push({ segments: [] });
  }

  // Compute gaps for each line
  const resultLines: GaugeLine[] = lines.map(line => {
    // Sort segments by start time
    const segs = [...line.segments].sort((a, b) => a.start.getTime() - b.start.getTime());

    // Build gaps
    const gaps: GaugeGap[] = [];
    let cursor = new Date(timelineStart);

    for (const seg of segs) {
      if (seg.start.getTime() > cursor.getTime()) {
        gaps.push({
          start: new Date(cursor),
          end: new Date(seg.start),
          isClickable: isInAnyActiveZone(cursor, seg.start, activeZones),
        });
      }
      cursor = new Date(Math.max(cursor.getTime(), seg.end.getTime()));
    }

    if (cursor.getTime() < timelineEnd.getTime()) {
      gaps.push({
        start: new Date(cursor),
        end: new Date(timelineEnd),
        isClickable: isInAnyActiveZone(cursor, timelineEnd, activeZones),
      });
    }

    return { segments: segs, gaps };
  });

  const totalAssigned = allPointShifts.length;

  return {
    pointId,
    pointName,
    pointIcon,
    isStage,
    isMeal,
    isCompact,
    categoryName,
    timelineStart,
    timelineEnd,
    activeZones,
    inactiveZones,
    lines: resultLines,
    maxRequiredTeam,
    totalAssigned,
  };
}

function isInAnyActiveZone(
  start: Date,
  end: Date,
  activeZones: { start: Date; end: Date }[],
): boolean {
  return activeZones.some(zone => start < zone.end && end > zone.start);
}

// ─── Team popover ──────────────────────────────────────────────────────────

function TeamPopover({
  segment,
  position,
  onEdit,
  onDelete,
  onClose,
}: {
  segment: GaugeSegment;
  position: { x: number; y: number };
  onEdit: (shift: Shift) => void;
  onDelete: (shiftId: string) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  const s = segment.shift;
  const team = segment.team;

  return (
    <div
      ref={ref}
      style={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        zIndex: 60,
        minWidth: 200,
      }}
      className="bg-[#1A1D23] border border-[#2D3139] rounded-xl shadow-2xl overflow-hidden"
    >
      {/* Team info header */}
      <div className="flex items-center gap-3 p-3 border-b border-[#2D3139]">
        {team.photo
          ? <img src={team.photo} className="w-9 h-9 rounded-full object-cover shrink-0" alt="" />
          : <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0" style={{ background: team.color || '#6366f1' }}>{team.name[0]}</div>
        }
        <div className="min-w-0">
          <p className="text-sm font-bold text-white truncate">{team.name}</p>
          <p className="text-[10px] text-slate-400">
            {team.isArtist ? 'Artiste' : 'Team'}
            {(team.roles || []).length > 0 ? ` · ${(team.roles || []).join(', ')}` : ''}
          </p>
        </div>
      </div>

      {/* Time info */}
      <div className="px-3 py-2 border-b border-[#2D3139] flex items-center gap-2">
        <Clock className="w-3.5 h-3.5 text-slate-500" />
        <span className="text-[11px] text-slate-300 font-mono">
          {fmtHour(segment.start)} – {fmtHour(segment.end)}
        </span>
      </div>

      {/* Actions */}
      <div className="flex divide-x divide-[#2D3139]">
        <button
          onClick={() => onEdit(s)}
          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-[11px] text-slate-300 hover:bg-[#2D3139] hover:text-white font-medium transition-colors"
        >
          <Pencil className="w-3.5 h-3.5" /> Modifier
        </button>
        <button
          onClick={() => onDelete(s.id)}
          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-[11px] text-red-400 hover:bg-red-500/10 hover:text-red-300 font-medium transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" /> Supprimer
        </button>
      </div>
    </div>
  );
}

// ─── Slot Gauge component ───────────────────────────────────────────────────

function SlotGauge({
  gauge,
  onEmptyClick,
  onSegmentClick,
  onDeleteShift,
}: {
  gauge: GaugeData;
  onEmptyClick: (slotStart: Date, slotEnd: Date) => void;
  onSegmentClick: (segment: GaugeSegment, rect: DOMRect) => void;
  onDeleteShift: (shiftId: string) => void;
}) {
  const totalDuration = gauge.timelineEnd.getTime() - gauge.timelineStart.getTime();
  if (totalDuration <= 0) return null;

  const toPct = (d: Date) => ((d.getTime() - gauge.timelineStart.getTime()) / totalDuration) * 100;
  const toPctWidth = (s: Date, e: Date) => ((e.getTime() - s.getTime()) / totalDuration) * 100;

  // Generate hour ticks
  // Ticks horaires. On part de l'heure pleine SUIVANTE si le créneau ne commence pas
  // à l'heure : sinon le premier tick tombe avant le début de l'axe, sort du cadre et
  // se fait rogner par le conteneur scrollable — ce qui décalait toute la lecture.
  const ticks: Date[] = [];
  const t = new Date(gauge.timelineStart);
  if (t.getMinutes() || t.getSeconds()) {
    t.setMinutes(0, 0, 0);
    t.setHours(t.getHours() + 1);
  } else {
    t.setMinutes(0, 0, 0);
  }
  while (t <= gauge.timelineEnd) {
    ticks.push(new Date(t));
    t.setHours(t.getHours() + 1);
  }

  // Repères de jour — minuit LOCAL, comme la timeline (un minuit UTC décalait
  // les libellés d'une heure pleine par rapport à l'axe horaire).
  const dayMarkers: Date[] = [];
  const dm = new Date(gauge.timelineStart.getFullYear(), gauge.timelineStart.getMonth(), gauge.timelineStart.getDate());
  if (dm < gauge.timelineStart) dm.setDate(dm.getDate() + 1);
  while (dm <= gauge.timelineEnd) {
    dayMarkers.push(new Date(dm));
    dm.setDate(dm.getDate() + 1);
  }

  const DAY_LABEL_H = 20;
  const LINE_H = 40;
  const TICK_H = 22;

  const handleGapClick = (gap: GaugeGap, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!gap.isClickable) return;
    // L'affectation couvre TOUTE la plage libre cliquée : si un membre occupe déjà une
    // partie du créneau, on complète la partie manquante ; si le créneau est vide, la
    // plage libre est le créneau entier. Le formulaire reste modifiable ensuite.
    onEmptyClick(gap.start, gap.end);
  };

  const gaugeWidthHours = totalDuration / 3600000;
  const gaugeMinWidth = Math.max(gaugeWidthHours * 70, 200); // px per hour, min 200px

  return (
    <div className="relative" style={{ minWidth: gaugeMinWidth }} data-gauge-area>
      {/* Day labels at top */}
      {dayMarkers.length >= 1 && (
        <div className="relative mx-2" style={{ height: DAY_LABEL_H }}>
          {dayMarkers.map((day, i) => (
            <div
              key={i}
              className="absolute top-0 flex flex-col items-center"
              style={{ left: `${toPct(day)}%`, transform: 'translateX(-50%)' }}
            >
              <span className="text-[9px] text-slate-400 font-medium whitespace-nowrap capitalize">
                {fmtDayShort(day.toISOString().split('T')[0])}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Active/Inactive zone background */}
      <div className="relative rounded-lg overflow-hidden border border-[#1F2937] bg-[#0A0B0E]">
        {/* Inactive zones */}
        {gauge.inactiveZones.map((zone, i) => (
          <div
            key={`inactive-${i}`}
            className="absolute top-0 bottom-0 bg-slate-800/20"
            style={{
              left: `${toPct(zone.start)}%`,
              width: `${toPctWidth(zone.start, zone.end)}%`,
              backgroundImage: 'repeating-linear-gradient(-45deg, transparent, transparent 3px, rgba(148,163,184,0.05) 3px, rgba(148,163,184,0.05) 6px)',
            }}
          />
        ))}

        {/* Day separator lines (subtle vertical lines at midnights) */}
        {dayMarkers.length >= 1 && dayMarkers.slice(1).map((day, i) => (
          <div
            key={`daysep-${i}`}
            className="absolute top-0 bottom-0 w-px bg-slate-500/30 z-[1]"
            style={{ left: `${toPct(day)}%` }}
          />
        ))}

        {/* Lines */}
        <div className="relative p-1.5 space-y-[3px]">
          {gauge.lines.map((line, li) => (
            <div
              key={li}
              className="relative rounded-md bg-white/[0.02] border border-white/5 hover:border-white/10 transition-colors"
              style={{ height: LINE_H }}
            >
              {/* Segments (team shifts) */}
              {line.segments.map((seg, si) => (
                <div
                  key={`seg-${si}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                    onSegmentClick(seg, rect);
                  }}
                  onContextMenu={(e) => {
                    // Clic droit : retirer le membre du créneau, après confirmation
                    e.preventDefault();
                    e.stopPropagation();
                    if (window.confirm(`Retirer ${seg.team.name} de ce créneau ?`)) {
                      onDeleteShift(seg.shift.id);
                    }
                  }}
                  className={`absolute top-0.5 bottom-0.5 rounded-md flex items-center gap-2 px-2 cursor-pointer transition-opacity hover:opacity-90 overflow-hidden border ${
                    seg.isActive ? 'opacity-100' : 'opacity-40'
                  }`}
                  style={{
                    left: `${toPct(seg.start)}%`,
                    width: `${toPctWidth(seg.start, seg.end)}%`,
                    // Garde-fou en PIXELS : un pourcentage minimum élargirait le bloc vers
                    // la droite et ferait chevaucher les zones voisines.
                    minWidth: 6,
                    backgroundColor: seg.isActive ? (seg.team.color || '#6366f1') + '66' : '#37415166',
                    borderColor: seg.isActive ? (seg.team.color || '#6366f1') : '#4B5563',
                  }}
                  title={`${seg.team.name} · ${fmtHour(seg.start)} – ${fmtHour(seg.end)} — clic droit pour retirer`}
                >
                  {/* Team avatar */}
                  {seg.team.photo
                    ? <img src={seg.team.photo} className="w-5 h-5 rounded-full object-cover shrink-0" alt="" />
                    : <div className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold text-white shrink-0" style={{ background: seg.team.color || '#6366f1' }}>{seg.team.name[0]}</div>
                  }
                  {/* Team name (hide on very narrow segments) */}
                  <span className="text-[10px] font-bold text-white truncate leading-none">
                    {seg.team.name.split(' ')[0]}
                  </span>
                </div>
              ))}

              {/* Gaps (clickable empty areas) */}
              {line.gaps.map((gap, gi) => (
                <div
                  key={`gap-${gi}`}
                  onClick={(e) => handleGapClick(gap, e)}
                  className={`absolute top-0.5 bottom-0.5 rounded-md transition-colors ${
                    gap.isClickable
                      ? 'cursor-pointer hover:bg-indigo-500/15'
                      : 'cursor-default'
                  }`}
                  style={{
                    left: `${toPct(gap.start)}%`,
                    width: `${toPctWidth(gap.start, gap.end)}%`,
                    minWidth: 6,
                  }}
                  title={gap.isClickable ? 'Cliquer pour assigner un team' : ''}
                />
              ))}

              {/* Empty state hint: show on first line if no segments */}
              {li === 0 && line.segments.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <span className="text-[9px] text-slate-600 italic">
                    Cliquer pour assigner un team
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Time ticks */}
      <div className="relative mt-0.5 mx-2" style={{ height: TICK_H }}>
        {ticks.map((tick, i) => (
          <div
            key={i}
            className="absolute top-0 flex flex-col items-center"
            style={{ left: `${toPct(tick)}%`, transform: 'translateX(-50%)' }}
          >
            <div className="w-px h-1.5 bg-[#2D3139]" />
            <span className="text-[8px] text-slate-600 font-mono mt-0.5 whitespace-nowrap">
              {fmtHour(tick)}
            </span>
          </div>
        ))}
      </div>

      {/* Active zone indicators */}
      <div className="relative mx-2 h-3 mt-0.5">
        {gauge.activeZones.map((zone, i) => (
          <div
            key={`zoneind-${i}`}
            className="absolute top-0 h-full flex items-center"
            style={{
              left: `${toPct(zone.start)}%`,
              width: `${toPctWidth(zone.start, zone.end)}%`,
            }}
          >
            <div className="w-full h-px bg-emerald-500/20" />
          </div>
        ))}
        {gauge.inactiveZones.map((zone, i) => (
          <div
            key={`inactind-${i}`}
            className="absolute top-0 h-full flex items-center"
            style={{
              left: `${toPct(zone.start)}%`,
              width: `${toPctWidth(zone.start, zone.end)}%`,
            }}
          >
            <div className="w-full h-px bg-slate-700/40 border-dashed" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Edit shift modal ───────────────────────────────────────────────────────

function EditShiftModal({
  shift,
  points,
  team,
  pointsMap,
  onSave,
  onDelete,
  onClose,
}: {
  shift: Shift;
  points: any[];
  team: Team[];
  pointsMap: Map<string, any>;
  onSave: (id: string, updated: Partial<Shift>) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
}) {
  const [editShift, setEditShift] = useState({ ...shift });
  const [isDeleting, setIsDeleting] = useState(false);

  const selectedPt = pointsMap.get(editShift.pointId);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#14161B] border border-[#2D3139] w-full max-w-md rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-[#2D3139] bg-[#1A1D23] flex justify-between items-center">
          <h3 className="text-base font-bold text-white">Modifier le Shift</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-[#2D3139] rounded-lg text-slate-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto custom-scrollbar">
          {/* Point Select */}
          <div className="space-y-1">
            <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Point de mission</label>
            <select
              value={editShift.pointId}
              onChange={e => setEditShift({ ...editShift, pointId: e.target.value })}
              className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-lg px-3 py-2 text-white text-xs focus:border-indigo-500 focus:outline-none"
            >
              {points.map((p: any) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>

          {/* Team Select */}
          <div className="space-y-1">
            <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Membre Team</label>
            <select
              value={editShift.teamId}
              onChange={e => setEditShift({ ...editShift, teamId: e.target.value })}
              className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-lg px-3 py-2 text-white text-xs focus:border-indigo-500 focus:outline-none"
            >
              {(selectedPt?.isStage ? team.filter(s => s.isArtist && !s.parentArtistId) : team.filter(s => !s.parentArtistId)).map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          {/* Times */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Début</label>
              <input
                type="datetime-local"
                value={editShift.start.substring(0, 16)}
                onChange={e => setEditShift({ ...editShift, start: e.target.value })}
                className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-lg px-3 py-2 text-white text-xs focus:border-indigo-500 focus:outline-none [color-scheme:dark]"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Fin</label>
              <input
                type="datetime-local"
                value={editShift.end.substring(0, 16)}
                onChange={e => setEditShift({ ...editShift, end: e.target.value })}
                className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-lg px-3 py-2 text-white text-xs focus:border-indigo-500 focus:outline-none [color-scheme:dark]"
              />
            </div>
          </div>

          {/* Delete section */}
          {isDeleting ? (
            <div className="bg-red-500/10 border border-red-500/30 p-4 rounded-xl space-y-3">
              <p className="text-xs text-red-400 font-medium text-center">Supprimer ce shift ?</p>
              <div className="flex gap-2">
                <button onClick={() => setIsDeleting(false)} className="flex-1 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-xs font-bold">Annuler</button>
                <button onClick={() => { onDelete(shift.id); onClose(); }} className="flex-1 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-xs font-bold">Supprimer</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setIsDeleting(true)} className="w-full py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 rounded-lg text-xs font-bold transition-all">
              Supprimer ce shift
            </button>
          )}
        </div>

        <div className="p-5 bg-[#1A1D23] border-t border-[#2D3139] flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 bg-[#0A0B0E] hover:bg-[#2D3139] text-slate-300 rounded-lg text-xs font-bold">Annuler</button>
          <button onClick={() => { onSave(shift.id, editShift); onClose(); }} className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-xs font-bold">
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MultiSlotPicker ────────────────────────────────────────────────────────

export function MultiSlotPicker({
  pointName,
  existingDates,
  presetDate,
  defaultStart,
  defaultEnd,
  onAdd,
  onClose,
}: {
  pointName: string;
  existingDates: Set<string>; // dates YYYY-MM-DD qui ont déjà des créneaux
  /** Jour pré-sélectionné à l'ouverture — celui affiché dans le planificateur. */
  presetDate?: string;
  /** Heures du point, reprises par défaut pour un nouveau créneau. Modifiables. */
  defaultStart?: string;
  defaultEnd?: string;
  onAdd: (slots: { start: string; end: string; requiredTeam: number }[]) => void;
  onClose: () => void;
}) {
  const today = new Date();
  const preset = presetDate ? new Date(presetDate + 'T00:00:00') : null;
  const [calMonth, setCalMonth] = useState(preset ? preset.getMonth() : today.getMonth());
  const [calYear, setCalYear] = useState(preset ? preset.getFullYear() : today.getFullYear());
  const [selectedDates, setSelectedDates] = useState<Set<string>>(presetDate ? new Set([presetDate]) : new Set());
  // Les horaires du point sont repris par défaut : un lieu qui ouvre à 19:30 doit
  // proposer 19:30 pour son nouveau créneau, pas 08:00. Reste modifiable.
  const [timeRanges, setTimeRanges] = useState<{ start: string; end: string; requiredTeam: number }[]>([
    { start: defaultStart || '08:00', end: defaultEnd || '18:00', requiredTeam: 1 }
  ]);

  // Build calendar grid for current month
  const calendarDays = useMemo(() => {
    const firstDay = new Date(calYear, calMonth, 1);
    const lastDay = new Date(calYear, calMonth + 1, 0);
    const startPad = (firstDay.getDay() + 6) % 7; // Monday start
    const days: (number | null)[] = [];
    for (let i = 0; i < startPad; i++) days.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) days.push(d);
    return days;
  }, [calMonth, calYear]);

  const toggleDate = (day: number) => {
    const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDates(prev => {
      const next = new Set(prev);
      if (next.has(dateStr)) next.delete(dateStr);
      else next.add(dateStr);
      return next;
    });
  };

  const selectWeekdays = () => {
    const newSet = new Set<string>();
    const lastDay = new Date(calYear, calMonth + 1, 0);
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const dow = new Date(calYear, calMonth, d).getDay();
      if (dow !== 0 && dow !== 6) { // Mon-Fri
        newSet.add(`${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
      }
    }
    setSelectedDates(newSet);
  };

  const selectAll = () => {
    const newSet = new Set<string>();
    const lastDay = new Date(calYear, calMonth + 1, 0);
    for (let d = 1; d <= lastDay.getDate(); d++) {
      newSet.add(`${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
    }
    setSelectedDates(newSet);
  };

  const sortedDates = Array.from(selectedDates).sort();
  const validRanges = timeRanges.filter(tr => tr.start && tr.end);
  const totalSlots = sortedDates.length * validRanges.length;

  const handleAdd = () => {
    if (sortedDates.length === 0 || timeRanges.length === 0) return;
    const validRanges = timeRanges.filter(tr => tr.start && tr.end);
    if (validRanges.length === 0) return;
    const slots: { start: string; end: string; requiredTeam: number }[] = [];
    sortedDates.forEach(date => {
      validRanges.forEach(tr => {
        slots.push({
          start: `${date}T${tr.start}`,
          end: `${date}T${tr.end}`,
          requiredTeam: tr.requiredTeam,
        });
      });
    });
    onAdd(slots);
  };

  const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  const dayHeaders = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[150] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto custom-scrollbar" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-4 border-b border-[#2D3139] flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-white">Ajouter des créneaux</h3>
            <p className="text-[10px] text-slate-400 mt-0.5 truncate">{pointName}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[#2D3139] rounded-lg text-slate-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Month navigation */}
          <div className="flex items-center justify-between">
            <button onClick={() => { if (calMonth === 0) { setCalMonth(11); setCalYear(calYear - 1); } else setCalMonth(calMonth - 1); }}
              className="p-1 hover:bg-[#2D3139] rounded text-slate-400 hover:text-white">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm font-bold text-white">{monthNames[calMonth]} {calYear}</span>
            <button onClick={() => { if (calMonth === 11) { setCalMonth(0); setCalYear(calYear + 1); } else setCalMonth(calMonth + 1); }}
              className="p-1 hover:bg-[#2D3139] rounded text-slate-400 hover:text-white">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Quick select */}
          <div className="flex gap-1.5">
            <button onClick={selectWeekdays}
              className="text-[10px] px-2 py-1 bg-[#1A1D23] hover:bg-indigo-500/20 border border-[#2D3139] hover:border-indigo-500/30 text-slate-400 hover:text-indigo-400 rounded-lg font-medium transition-all">
              Jours ouvrés
            </button>
            <button onClick={selectAll}
              className="text-[10px] px-2 py-1 bg-[#1A1D23] hover:bg-indigo-500/20 border border-[#2D3139] hover:border-indigo-500/30 text-slate-400 hover:text-indigo-400 rounded-lg font-medium transition-all">
              Tout le mois
            </button>
            <button onClick={() => setSelectedDates(new Set())}
              className="text-[10px] px-2 py-1 bg-[#1A1D23] hover:bg-red-500/20 border border-[#2D3139] hover:border-red-500/30 text-slate-400 hover:text-red-400 rounded-lg font-medium transition-all">
              Effacer
            </button>
          </div>

          {/* Calendar grid */}
          <div className="bg-[#0A0B0E] border border-[#2D3139] rounded-xl overflow-hidden">
            <div className="grid grid-cols-7 gap-px bg-[#2D3139]">
              {dayHeaders.map(dh => (
                <div key={dh} className="bg-[#0A0B0E] text-center py-1.5 text-[10px] font-bold text-slate-500">{dh}</div>
              ))}
              {calendarDays.map((day, i) => {
                if (day === null) return <div key={`pad-${i}`} className="bg-[#0A0B0E]" />;
                const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const isSelected = selectedDates.has(dateStr);
                const isExisting = existingDates.has(dateStr);
                const isPast = new Date(calYear, calMonth, day) < new Date(today.getFullYear(), today.getMonth(), today.getDate());
                return (
                  <button
                    key={dateStr}
                    onClick={() => !isPast && toggleDate(day)}
                    disabled={isPast}
                    className={`bg-[#0A0B0E] text-center py-1.5 text-[11px] transition-colors relative
                      ${isPast ? 'text-slate-700 cursor-default' : 'hover:bg-[#1A1D23] cursor-pointer'}
                      ${isSelected ? 'bg-indigo-500/30 text-indigo-300 font-bold ring-1 ring-indigo-500/50' : 'text-slate-300'}
                      ${isExisting && !isSelected ? 'text-emerald-400' : ''}`}
                  >
                    {day}
                    {isExisting && !isSelected && (
                      <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-emerald-400" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Time ranges */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Créneaux horaires</span>
              <button
                onClick={() => setTimeRanges([...timeRanges, { start: '08:00', end: '18:00', requiredTeam: 1 }])}
                className="text-[10px] text-indigo-400 hover:text-indigo-300 font-medium flex items-center gap-1"
              >
                <Plus className="w-3 h-3" /> Ajouter
              </button>
            </div>
            {timeRanges.map((tr, idx) => (
              <div key={idx} className="flex items-center gap-1.5 bg-[#0A0B0E] border border-[#2D3139] rounded-lg px-2 py-1.5">
                <input type="time" value={tr.start}
                  onChange={e => {
                    const copy = [...timeRanges];
                    copy[idx] = { ...copy[idx], start: e.target.value };
                    setTimeRanges(copy);
                  }}
                  className="w-[90px] bg-transparent text-xs text-white focus:outline-none [color-scheme:dark]" />
                <span className="text-slate-600 text-[10px]">→</span>
                <input type="time" value={tr.end}
                  onChange={e => {
                    const copy = [...timeRanges];
                    copy[idx] = { ...copy[idx], end: e.target.value };
                    setTimeRanges(copy);
                  }}
                  className="w-[90px] bg-transparent text-xs text-white focus:outline-none [color-scheme:dark]" />
                <span className="text-[9px] text-slate-500 ml-1">×</span>
                <input type="number" min={1} max={20} value={tr.requiredTeam}
                  onChange={e => {
                    const copy = [...timeRanges];
                    copy[idx] = { ...copy[idx], requiredTeam: parseInt(e.target.value) || 1 };
                    setTimeRanges(copy);
                  }}
                  className="w-10 bg-transparent text-xs text-white focus:outline-none text-center"
                  title="Team requis" />
                {timeRanges.length > 1 && (
                  <button
                    onClick={() => setTimeRanges(timeRanges.filter((_, i) => i !== idx))}
                    className="ml-auto p-0.5 text-slate-600 hover:text-red-400 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Preview */}
          <div className="bg-[#0A0B0E] border border-[#2D3139] rounded-xl p-3">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-3.5 h-3.5 text-indigo-400" />
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Aperçu</span>
              <span className="text-[10px] text-indigo-400 font-bold ml-auto">{totalSlots} créneau{totalSlots > 1 ? 'x' : ''}</span>
            </div>
            {sortedDates.length === 0 || validRanges.length === 0 ? (
              <p className="text-[10px] text-slate-600 italic">
                {sortedDates.length === 0 ? 'Sélectionne des dates dans le calendrier' : 'Ajoute au moins un créneau horaire'}
              </p>
            ) : (
              <div className="max-h-32 overflow-y-auto custom-scrollbar space-y-1">
                {sortedDates.map(date => {
                  const d = new Date(date + 'T00:00:00');
                  const label = d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
                  return (
                    <div key={date} className="text-[10px]">
                      <span className="text-slate-500 font-medium">{label}</span>
                      {validRanges.map((tr, i) => (
                        <span key={i} className="text-indigo-400 font-mono ml-2">{tr.start}–{tr.end} ×{tr.requiredTeam}{i < validRanges.length - 1 ? ', ' : ''}</span>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="p-4 border-t border-[#2D3139] bg-[#0A0B0E] rounded-b-2xl flex gap-2">
          <button onClick={onClose}
            className="flex-1 py-2.5 bg-[#1A1D23] hover:bg-[#2D3139] text-slate-300 rounded-lg text-xs font-bold transition-colors">
            Annuler
          </button>
          <button onClick={handleAdd} disabled={sortedDates.length === 0 || validRanges.length === 0}
            className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-30 text-white rounded-lg text-xs font-bold transition-colors">
            Ajouter {totalSlots > 0 ? `${totalSlots} créneau${totalSlots > 1 ? 'x' : ''}` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────

interface Props {
  onNavigateBack: () => void;
}

export function SlotPlannerView({ onNavigateBack }: Props) {
  const { points: allPoints, tasks: allTasks, shifts, team: allTeam, addShift, updateShift, deleteShift, updatePoint, categories, currentUser, viewAsManagerCategoryId } = useVisibleStore();

  const categoriesMap = useMemo(() => new Map(categories.map(c => [c.id, c])), [categories]);
  const teamMap = useMemo(() => new Map(allTeam.map(s => [s.id, s])), [allTeam]);
  const pointsMap = useMemo(() => new Map(allPoints.map(p => [p.id, p])), [allPoints]);

  const effectiveCategoryId = (currentUser.role === 'manager' && currentUser.categoryId) || viewAsManagerCategoryId;
  const myCategory = effectiveCategoryId ? categoriesMap.get(effectiveCategoryId) : undefined;
  const isArtistManager = myCategory?.isArtistCategory === true;
  const isAdmin = currentUser.role === 'admin' && !viewAsManagerCategoryId;

  // Jour affiché par les barres. L'axe d'un gauge couvre une seule journée : sans cela
  // il s'étend sur toute la programmation du lieu et un créneau de 30 min devient un trait.
  const [selectedDay, setSelectedDay] = useState<string>('');

  // Jours portant au moins un créneau, points et tâches confondus
  const availableDays = useMemo(() => {
    const days = new Set<string>();
    allPoints.forEach(pt => (pt.openingHours || []).forEach(s => days.add(dayKey(new Date(s.start)))));
    (allTasks || []).forEach(t => (t.openingHours || []).forEach(s => days.add(dayKey(new Date(s.start)))));
    return Array.from(days).sort();
  }, [allPoints, allTasks]);

  // Par défaut : aujourd'hui s'il porte des créneaux, sinon le premier jour programmé
  useEffect(() => {
    if (selectedDay || availableDays.length === 0) return;
    const today = dayKey(new Date());
    setSelectedDay(availableDays.includes(today) ? today : availableDays[0]);
  }, [availableDays, selectedDay]);

  // Overlap / assign state
  const [overlapWarning, setOverlapWarning] = useState<{ teamId: string; teamName: string; pointName: string; overlapPointName?: string; overlapStart?: string; overlapEnd?: string } | null>(null);
  const [pendingAssign, setPendingAssign] = useState<{ teamId: string; pointId: string; start: Date; end: Date } | null>(null);

  // Multi-slot picker state
  const [multiSlotPicker, setMultiSlotPicker] = useState<{
    pointId: string;
    pointName: string;
    existingDates: Set<string>;
  } | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);

  // Assignment modal
  const [assignModal, setAssignModal] = useState<{ pointId: string; pointName: string; slotStart: Date; slotEnd: Date } | null>(null);
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [assignSearch, setAssignSearch] = useState('');
  const [assignCategoryFilter, setAssignCategoryFilter] = useState('');
  const [assignStartTime, setAssignStartTime] = useState('');
  const [assignEndTime, setAssignEndTime] = useState('');

  // Team popover state
  const [teamPopover, setTeamPopover] = useState<{
    segment: GaugeSegment;
    x: number; y: number;
  } | null>(null);

  // Edit shift modal state
  const [editShiftModal, setEditShiftModal] = useState<Shift | null>(null);

  // État d'expansion pour la vue compacte "Voir liste"
  const [expandedCompactPoints, setExpandedCompactPoints] = useState<Set<string>>(new Set());

  // ─── One continuous gauge per point (all days combined) ──────────────────

  const pointGauges = useMemo(() => {
    const result: GaugeData[] = [];

    allPoints.forEach(pt => {
      // Permission check
      if (pt.isStage && !isAdmin && !isArtistManager) return;

      const cat = pt.categoryId ? categoriesMap.get(pt.categoryId) : undefined;
      const catName = cat?.name || '';

      // Collect ALL slots for this point (no date filtering — continuous timeline)
      const pointSlots: { start: Date; end: Date; requiredTeam: number; isHard: boolean }[] = [];

      (pt.openingHours || []).forEach(slot => {
        // Parse as local time (same as shifts) — don't force UTC with 'Z'
        const slotStart = new Date(slot.start);
        const slotEnd = new Date(slot.end);
        pointSlots.push({
          start: slotStart,
          end: slotEnd,
          requiredTeam: slot.requiredTeam || pt.requiredTeam || 1,
          isHard: !!slot.isHard,
        });
      });

      // Collect ALL shifts for this point
      const pointShifts = shifts.filter(s => s.pointId === pt.id);

      // Un point est "compact" s'il a au maximum 1 créneau par jour calendaire
      // (ex: 08:00-18:00 chaque jour) vs plusieurs créneaux par jour (ex: soundcheck + concert)
      const slotsPerDay = new Map<string, number>();
      pointSlots.forEach(s => {
        const day = dayKey(s.start);
        slotsPerDay.set(day, (slotsPerDay.get(day) || 0) + 1);
      });
      const maxSlotsPerDay = Math.max(...slotsPerDay.values(), 0);
      const hasSingleDailySlot = pointSlots.length > 0 && maxSlotsPerDay <= 1;
      const isCompact = hasSingleDailySlot && !pt.isStage && !cat?.isArtistCategory;

      // L'axe d'un gauge couvre une seule journée : on ne lui passe que les créneaux de
      // ce jour. La vue compacte, elle, reste un résumé de tous les jours.
      const daySlots = selectedDay ? pointSlots.filter(s => dayKey(s.start) === selectedDay) : pointSlots;

      const gauge = buildGaugeData(
        pt.id,
        pt.name,
        pt.iconName || 'MapPin',
        !!pt.isStage,
        !!pt.isMealPoint,
        isCompact,
        catName,
        daySlots,
        pointShifts,
        teamMap,
      );

      if (gauge) {
        result.push(gauge);
      }
    });

    // Sort chronologically by earliest active zone start time
    return result.sort((a, b) =>
      a.timelineStart.getTime() - b.timelineStart.getTime()
    );
  }, [allPoints, shifts, categoriesMap, teamMap, isAdmin, isArtistManager, selectedDay]);

  // Task gauges — same logic as pointGauges but for tasks
  const taskGauges = useMemo(() => {
    const result: GaugeData[] = [];
    (allTasks || []).forEach(task => {
      const cat = task.categoryId ? categoriesMap.get(task.categoryId) : undefined;
      const catName = cat?.name || '';
      const taskSlots: { start: Date; end: Date; requiredTeam: number; isHard: boolean }[] = [];
      (task.openingHours || []).forEach(slot => {
        taskSlots.push({
          start: new Date(slot.start), end: new Date(slot.end),
          requiredTeam: slot.requiredTeam || task.requiredTeam || 1,
          isHard: !!slot.isHard,
        });
      });
      if (taskSlots.length === 0) return;
      const taskShifts = shifts.filter(s => s.taskId === task.id);
      // Même filtrage par jour que pour les points
      const dayTaskSlots = selectedDay ? taskSlots.filter(s => dayKey(s.start) === selectedDay) : taskSlots;
      const gauge = buildGaugeData(task.id, task.name, task.iconName || 'ClipboardList', false, false, true, catName, dayTaskSlots, taskShifts, teamMap);
      if (gauge) result.push(gauge);
    });
    return result.sort((a, b) => a.timelineStart.getTime() - b.timelineStart.getTime());
  }, [allTasks, shifts, categoriesMap, teamMap, selectedDay]);

  const allGauges = [...pointGauges, ...taskGauges].sort((a, b) => a.timelineStart.getTime() - b.timelineStart.getTime());
  // Split: points with slots first, empty ones at the bottom
  const activeGauges = allGauges.filter(g => !g.hasNoSlots);
  const emptyGauges = allGauges.filter(g => g.hasNoSlots);

  // ─── Handlers ────────────────────────────────────────────────────────────

  const handleAssign = (teamId: string, pointId: string, start: Date, end: Date, force = false) => {
    if (!force) {
      // Find the overlapping shift (could be on a different point)
      const overlapShift = shifts.find(s => {
        if (s.teamId !== teamId) return false;
        const sStart = new Date(s.start + (s.start.endsWith('Z') ? '' : 'Z'));
        const sEnd = new Date(s.end + (s.end.endsWith('Z') ? '' : 'Z'));
        return sStart < end && sEnd > start;
      });

      if (overlapShift) {
        const currentPt = allPoints.find(p => p.id === pointId);
        const overlapPt = allPoints.find(p => p.id === overlapShift.pointId);
        const fmtDate = (d: Date) => d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
        const fmtTime = (d: Date) => d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
        const oStart = new Date(overlapShift.start + (overlapShift.start.endsWith('Z') ? '' : 'Z'));
        const oEnd = new Date(overlapShift.end + (overlapShift.end.endsWith('Z') ? '' : 'Z'));
        setOverlapWarning({
          teamId,
          teamName: teamMap.get(teamId)?.name || '',
          pointName: currentPt?.name || '',
          overlapPointName: overlapPt?.name || '',
          overlapStart: `${fmtDate(oStart)} ${fmtTime(oStart)}`,
          overlapEnd: fmtTime(oEnd),
        });
        setPendingAssign({ teamId, pointId, start, end });
        return;
      }
    }

    // Store as local ISO string (no timezone conversion — consistent with how slots are now parsed)
    const fmtLocal = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const h = String(d.getHours()).padStart(2, '0');
      const min = String(d.getMinutes()).padStart(2, '0');
      return `${y}-${m}-${day}T${h}:${min}`;
    };
    addShift({ teamId, pointId, start: fmtLocal(start), end: fmtLocal(end), description: '' });
    setOverlapWarning(null);
    setPendingAssign(null);
  };

  const handleEmptyClick = (pointId: string, pointName: string, clickedTime: Date, zoneEnd: Date) => {
    setAssignModal({ pointId, pointName, slotStart: clickedTime, slotEnd: zoneEnd });
    setSelectedTeamIds([]);
    setAssignSearch('');
    setAssignCategoryFilter('');
    setAssignStartTime(fmtHour(clickedTime));
    setAssignEndTime(fmtHour(zoneEnd));
  };

  const handleSegmentClick = (segment: GaugeSegment, elemRect: DOMRect) => {
    setTeamPopover({
      segment,
      x: Math.min(elemRect.left, window.innerWidth - 220),
      y: Math.min(elemRect.bottom + 4, window.innerHeight - 180),
    });
  };

  // Filtered team for assignment modal (exclude sub-members)
  const visibleTeam = useMemo(() => {
    const selectedPoint = allPoints.find(p => p.id === assignModal?.pointId);
    const cat = selectedPoint?.categoryId ? categoriesMap.get(selectedPoint.categoryId) : undefined;
    if (selectedPoint?.isStage) return allTeam.filter(s => s.isArtist && !s.parentArtistId).sort((a, b) => a.name.localeCompare(b.name));
    if (cat?.isArtistCategory && selectedPoint) {
      return allTeam.filter(s => (s.isArtist && !s.parentArtistId) || s.categoryId === selectedPoint.categoryId).sort((a, b) => {
        if (a.isArtist !== b.isArtist) return a.isArtist ? -1 : 1;
        return a.name.localeCompare(b.name);
      });
    }
    return allTeam;
  }, [allTeam, assignModal?.pointId, allPoints, categoriesMap]);

  const filteredTeam = useMemo(() => {
    let result = visibleTeam;
    if (assignCategoryFilter) {
      result = result.filter(s => s.categoryId === assignCategoryFilter || (s.categoryIds && s.categoryIds.includes(assignCategoryFilter)));
    }
    if (assignSearch) {
      const q = assignSearch.toLowerCase();
      result = result.filter(s => s.name.toLowerCase().includes(q));
    }
    return result;
  }, [visibleTeam, assignSearch, assignCategoryFilter]);

  const totalAssigned = useMemo(() => {
    let count = 0;
    allGauges.forEach(g => count += g.totalAssigned);
    return count;
  }, [allGauges]);

  return (
    <div className="flex flex-col h-full bg-[#0A0B0E] overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-[#2D3139] flex-none">
        <button onClick={onNavigateBack} className="p-1.5 hover:bg-[#1A1D23] rounded-lg text-slate-400 hover:text-white">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h2 className="text-sm font-bold text-white">Créneaux</h2>

        <span className="text-[10px] text-slate-500 bg-slate-800/50 px-1.5 py-0.5 rounded">
          {allTeam.length} team · {totalAssigned} assignés
        </span>
        <div className="flex items-center gap-1 ml-auto">
          <button
            onClick={() => setShowTeamModal(true)}
            className="flex items-center gap-1 px-2.5 py-1.5 bg-[#1A1D23] hover:bg-emerald-500/20 border border-[#2D3139] hover:border-emerald-500/30 text-slate-400 hover:text-emerald-400 rounded-lg text-[10px] font-bold transition-all"
            title="Ajouter un team"
          >
            <UserPlus className="w-3.5 h-3.5" /> Team
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-1 px-2.5 py-1.5 bg-[#1A1D23] hover:bg-indigo-500/20 border border-[#2D3139] hover:border-indigo-500/30 text-slate-400 hover:text-indigo-400 rounded-lg text-[10px] font-bold transition-all"
          >
            <Plus className="w-3.5 h-3.5" /> Point / Tâche
          </button>
        </div>
        <AddPointTaskModal isOpen={showAddModal} onClose={() => setShowAddModal(false)} />
        <AddTeamModal isOpen={showTeamModal} onClose={() => setShowTeamModal(false)} />
      </div>

      {/* Sélecteur de jour : les barres se lisent à l'échelle d'une journée */}
      {availableDays.length > 1 && (
        <div className="flex items-center gap-1.5 px-3 py-2 border-b border-[#2D3139] flex-none overflow-x-auto custom-scrollbar">
          <Calendar className="w-3.5 h-3.5 text-slate-500 flex-none" />
          {availableDays.map(day => (
            <button
              key={day}
              onClick={() => setSelectedDay(day)}
              className={`px-2.5 py-1 rounded-lg text-[10px] font-bold whitespace-nowrap transition-colors border ${
                day === selectedDay
                  ? 'bg-indigo-500/20 border-indigo-500/40 text-indigo-300'
                  : 'bg-[#1A1D23] border-[#2D3139] text-slate-400 hover:text-white hover:border-slate-600'
              }`}
            >
              {fmtDayShort(day)}
            </button>
          ))}
        </div>
      )}

      {/* Point cards — vertically scrollable, each with one gauge covering the selected day */}
      <div className="flex-1 overflow-y-auto custom-scrollbar p-3 space-y-4">
        {activeGauges.length === 0 && emptyGauges.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm text-slate-500">Aucun créneau.</p>
          </div>
        ) : (
          <>
            {activeGauges.map(gauge => (
            <div key={gauge.pointId} className="bg-[#14161B] border border-[#2D3139] rounded-xl overflow-hidden">
              {/* Point header */}
              <div className="flex items-center gap-2 px-3 py-2 border-b border-[#1F2937] bg-[#0D0E12]">
                <PointIcon name={gauge.pointIcon} size={14} color={gauge.isStage ? '#F59E0B' : gauge.isMeal ? '#10B981' : '#6366F1'} />
                <span className="text-xs font-bold text-slate-200 truncate flex-1">{gauge.pointName}</span>
                {gauge.isStage && <span className="text-[8px] bg-amber-500/20 text-amber-400 px-1 py-0.5 rounded">Scene</span>}
                {gauge.isMeal && <span className="text-[8px] bg-emerald-500/20 text-emerald-400 px-1 py-0.5 rounded">Repas</span>}
                {gauge.isCompact && !gauge.isMeal && !gauge.isStage && <span className="text-[8px] bg-slate-500/20 text-slate-400 px-1 py-0.5 rounded">Tâche</span>}
                {gauge.hasNoSlots ? <span className="text-[8px] bg-red-500/20 text-red-400 px-1 py-0.5 rounded">Sans créneaux</span> : <span className={`text-[10px] font-bold ${gauge.totalAssigned >= gauge.maxRequiredTeam ? 'text-emerald-400' : gauge.totalAssigned > 0 ? 'text-amber-400' : 'text-red-400'}`}>
                  {gauge.totalAssigned}/{gauge.maxRequiredTeam} assignés
                </span>}
                <button
                  onClick={() => {
                    // Collecter les dates qui ont déjà des créneaux pour ce point
                    const existingDates = new Set<string>();
                    gauge.activeZones.forEach(zone => {
                      existingDates.add(zone.start.toISOString().split('T')[0]);
                    });
                    setMultiSlotPicker({
                      pointId: gauge.pointId,
                      pointName: gauge.pointName,
                      existingDates,
                    });
                  }}
                  className="p-1 rounded hover:bg-indigo-500/20 text-slate-500 hover:text-indigo-400 transition-colors"
                  title="Ajouter des créneaux (multi-dates)"
                >
                  <Calendar className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Continuous gauge — horizontally scrollable if wider than container */}
              <div className="overflow-x-auto custom-scrollbar">
                <div className="p-3">
                  {gauge.isCompact ? (
                    /* Vue compacte pour les points à 1 créneau/jour : compteur + Voir liste */
                    (() => {
                      const isExpanded = expandedCompactPoints.has(gauge.pointId);
                      // Collecter tous les shifts assignés avec leurs teams (dédupliqués par teamId)
                      const assignedTeams = gauge.lines.flatMap(line =>
                        line.segments.map(seg => ({
                          team: seg.team,
                          shift: seg.shift,
                          start: seg.start,
                          end: seg.end,
                        }))
                      ).filter((v, i, arr) => arr.findIndex(x => x.team.id === v.team.id) === i)
                       .sort((a, b) => a.team.name.localeCompare(b.team.name));

                      // Heures des créneaux (groupées par pattern horaire)
                      const timePatterns = new Map<string, { start: Date; end: Date; days: number; requiredTeam: number; dates: string[] }>();
                      gauge.activeZones.forEach(zone => {
                        const key = `${fmtHour(zone.start)}–${fmtHour(zone.end)}`;
                        if (!timePatterns.has(key)) {
                          timePatterns.set(key, { start: zone.start, end: zone.end, days: 0, requiredTeam: zone.requiredTeam, dates: [] });
                        }
                        const pattern = timePatterns.get(key)!;
                        pattern.days++;
                        // Jours distincts couverts par ce pattern horaire, pour pouvoir les afficher
                        const day = dayKey(zone.start);
                        if (!pattern.dates.includes(day)) pattern.dates.push(day);
                      });
                      timePatterns.forEach(p => p.dates.sort());

                      return (
                        <div className="space-y-2">
                          {/* Résumé compact */}
                          <div className="flex items-center gap-2 flex-wrap">
                            {Array.from(timePatterns.entries()).map(([key, pat]) => (
                              <span key={key} className="text-[11px] font-mono text-slate-300 bg-[#0A0B0E] border border-[#1F2937] rounded-md px-2 py-0.5">
                                {/* Date(s) du créneau, puis ses horaires */}
                                <span className="text-slate-400">
                                  {pat.dates.length === 1
                                    ? fmtDayShort(pat.dates[0])
                                    : `${fmtDayShort(pat.dates[0])} – ${fmtDayShort(pat.dates[pat.dates.length - 1])}`}
                                </span>
                                <span className="text-slate-600 mx-1.5">·</span>
                                {key}
                                {pat.days > 1 && <span className="text-slate-500 ml-1">×{pat.days}j</span>}
                              </span>
                            ))}
                            <span className={`text-[11px] font-bold ${gauge.totalAssigned >= gauge.maxRequiredTeam ? 'text-emerald-400' : gauge.totalAssigned > 0 ? 'text-amber-400' : 'text-red-400'}`}>
                              {gauge.totalAssigned}/{gauge.maxRequiredTeam} assignés
                            </span>
                            <button
                              onClick={() => {
                                setExpandedCompactPoints(prev => {
                                  const next = new Set(prev);
                                  if (next.has(gauge.pointId)) next.delete(gauge.pointId);
                                  else next.add(gauge.pointId);
                                  return next;
                                });
                              }}
                              className="ml-auto inline-flex items-center gap-1 text-[11px] text-indigo-400 hover:text-indigo-300 font-medium transition-colors px-2 py-0.5 rounded-full hover:bg-indigo-500/10"
                            >
                              {isExpanded ? (
                                <><ChevronDown className="w-3 h-3" /> Masquer</>
                              ) : (
                                <><ChevronRight className="w-3 h-3" /> Voir liste{assignedTeams.length > 0 ? ` (${assignedTeams.length})` : ''}</>
                              )}
                            </button>
                            <button
                              onClick={() => {
                                const firstZone = gauge.activeZones[0];
                                if (firstZone) {
                                  handleEmptyClick(gauge.pointId, gauge.pointName, new Date(firstZone.start), new Date(firstZone.end));
                                }
                              }}
                              className="inline-flex items-center gap-1 text-[10px] text-indigo-400 hover:text-indigo-300 font-medium transition-colors px-1.5 py-0.5 rounded-full hover:bg-indigo-500/10"
                            >
                              <Plus className="w-3 h-3" /> Ajouter
                            </button>
                          </div>

                          {/* Liste dépliée des personnes assignées */}
                          {isExpanded && (
                            <div className="space-y-1 border-t border-[#1F2937] pt-2">
                              {assignedTeams.length === 0 ? (
                                <p className="text-[10px] text-slate-500 italic py-1">Aucun team assigné</p>
                              ) : (
                                assignedTeams.map(({ team, shift, start, end }) => (
                                  <div
                                    key={team.id}
                                    onClick={(e) => {
                                      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                      const segment: GaugeSegment = {
                                        start,
                                        end,
                                        shift,
                                        team,
                                        isActive: true,
                                      };
                                      handleSegmentClick(segment, rect);
                                    }}
                                    className="flex items-center gap-2 py-1.5 px-2 rounded-md bg-[#0A0B0E] border border-[#1F2937] hover:border-[#2D3139] cursor-pointer transition-colors group"
                                  >
                                    {team.photo
                                      ? <img src={team.photo} className="w-5 h-5 rounded-full object-cover shrink-0" alt="" />
                                      : <div className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold text-white shrink-0" style={{ background: team.color || '#6366f1' }}>{team.name[0]}</div>
                                    }
                                    <span className="text-[11px] text-slate-200 truncate flex-1">{team.name}</span>
                                    <span className="text-[10px] font-mono text-slate-500 shrink-0">
                                      {fmtHour(start)}–{fmtHour(end)}
                                    </span>
                                    <span className="text-[9px] text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                                      <Pencil className="w-3 h-3" />
                                    </span>
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()
                  ) : (
                    /* Full continuous gauge for artist & stage categories */
                    <>
                      <SlotGauge
                        gauge={gauge}
                        onEmptyClick={(clickedTime, zoneEnd) =>
                          handleEmptyClick(gauge.pointId, gauge.pointName, clickedTime, zoneEnd)
                        }
                        onSegmentClick={(segment, rect) => handleSegmentClick(segment, rect)}
                        onDeleteShift={deleteShift}
                      />
                      {/* Assigned team avatars */}
                      <div className="flex items-center gap-1 mt-2.5">
                        {gauge.lines.flatMap(line => line.segments.map(seg => seg.team)).filter((st, i, arr) => arr.findIndex(s => s.id === st.id) === i).map(st => (
                          <div key={st.id} className="shrink-0" title={st.name}>
                            {st.photo
                              ? <img src={st.photo} className="w-5 h-5 rounded-full object-cover border-2" style={{ borderColor: st.color || '#6366f1' }} alt={st.name} />
                              : <div className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold text-white border-2" style={{ background: st.color || '#6366f1', borderColor: st.color || '#6366f1' }}>{st.name[0]}</div>
                            }
                          </div>
                        ))}
                        {gauge.totalAssigned === 0 && (
                          <span className="text-[9px] text-slate-600 italic">Aucun team assigné</span>
                        )}
                        <button
                          onClick={() => {
                            const firstZone = gauge.activeZones[0];
                            if (firstZone) {
                              handleEmptyClick(gauge.pointId, gauge.pointName, new Date(firstZone.start), new Date(firstZone.end));
                            }
                          }}
                          className="inline-flex items-center gap-1 text-[10px] text-indigo-400 hover:text-indigo-300 font-medium transition-colors ml-1 px-1.5 py-1 rounded-full hover:bg-indigo-500/10"
                        >
                          <Plus className="w-3 h-3" /> Ajouter
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
          {/* Empty slots section — editable to add slots */}
          {emptyGauges.length > 0 && (
            <div className="mt-4 pt-4 border-t border-[#2D3139]/50">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-3">Sans créneaux ({emptyGauges.length})</p>
              {emptyGauges.map(gauge => (
                <div key={gauge.pointId} className="bg-[#14161B] border border-[#2D3139]/50 rounded-xl overflow-hidden mb-3 opacity-60 hover:opacity-100 transition-opacity">
                  <div className="flex items-center gap-2 px-3 py-2 border-b border-[#1F2937] bg-[#0D0E12]">
                    <PointIcon name={gauge.pointIcon} size={14} color={gauge.isStage ? '#F59E0B' : gauge.isMeal ? '#10B981' : '#6366F1'} />
                    <span className="text-xs font-bold text-slate-200 truncate flex-1">{gauge.pointName}</span>
                    {gauge.isStage && <span className="text-[8px] bg-amber-500/20 text-amber-400 px-1 py-0.5 rounded">Scene</span>}
                    {gauge.isMeal && <span className="text-[8px] bg-emerald-500/20 text-emerald-400 px-1 py-0.5 rounded">Repas</span>}
                    <span className="text-[8px] bg-red-500/20 text-red-400 px-1 py-0.5 rounded">Vide</span>
                    <button
                      onClick={() => {
                        setMultiSlotPicker({
                          pointId: gauge.pointId,
                          pointName: gauge.pointName,
                          existingDates: new Set(),
                        });
                      }}
                      className="flex items-center gap-1 px-2 py-1 rounded-lg bg-indigo-500/15 hover:bg-indigo-500/25 border border-indigo-500/30 text-indigo-300 text-[10px] font-bold transition-colors"
                      title="Créer un créneau pour ce point"
                    >
                      <Calendar className="w-3 h-3" /> Créer un créneau
                    </button>
                  </div>
                  <p className="px-3 py-1.5 text-[9px] text-slate-500 italic">
                    Un créneau est nécessaire pour pouvoir y affecter un team.
                  </p>
                </div>
              ))}
            </div>
          )}
          </>
        )}
      </div>

      {/* Assignment Modal */}
      {assignModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setAssignModal(null)}>
          <div className="bg-[#14161B] border border-[#2D3139] rounded-xl w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-[#2D3139]">
              <h3 className="text-sm font-bold text-white">Assigner un team</h3>
              <p className="text-[10px] text-slate-400 mt-0.5">{assignModal.pointName} · {assignStartTime} – {assignEndTime}</p>
            </div>

            <div className="p-4 space-y-3">
              {/* Team search */}
              <div>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                  <input
                    type="text" placeholder="Rechercher un team..."
                    value={assignSearch} onChange={e => setAssignSearch(e.target.value)}
                    autoFocus
                    className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-lg pl-8 pr-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500"
                  />
                </div>
              </div>

              {/* Category filter */}
              {(categories.filter(c => !c.isGeneral).length > 1) && (
                <select
                  value={assignCategoryFilter}
                  onChange={e => setAssignCategoryFilter(e.target.value)}
                  className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-indigo-500 appearance-none"
                >
                  <option value="">Toutes les catégories</option>
                  {categories.filter(c => !c.isGeneral).map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              )}

              {/* Select all / deselect all */}
              {filteredTeam.length > 0 && (
                <button
                  onClick={() => {
                    if (selectedTeamIds.length === filteredTeam.length) {
                      setSelectedTeamIds([]);
                    } else {
                      setSelectedTeamIds(filteredTeam.map(m => m.id));
                    }
                  }}
                  className="w-full text-[10px] text-indigo-400 hover:text-indigo-300 hover:bg-[#1A1D23] py-1.5 rounded-lg transition-colors"
                >
                  {selectedTeamIds.length === filteredTeam.length ? 'Désélectionner tout' : `Sélectionner tout (${filteredTeam.length})`}
                </button>
              )}

              {/* Team list */}
              <div className="max-h-48 overflow-y-auto custom-scrollbar border border-[#2D3139] rounded-lg bg-[#0A0B0E] divide-y divide-[#1F2937]">
                {filteredTeam.length === 0 ? (
                  <p className="text-[10px] text-slate-500 p-3 text-center">Aucun résultat</p>
                ) : (
                  filteredTeam.map(member => {
                    const isSelected = selectedTeamIds.includes(member.id);
                    return (
                    <button
                      key={member.id}
                      onClick={() => {
                        setSelectedTeamIds(prev =>
                          prev.includes(member.id)
                            ? prev.filter(id => id !== member.id)
                            : [...prev, member.id]
                        );
                      }}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 hover:bg-[#1A1D23] transition-colors text-left ${isSelected ? 'bg-indigo-500/20 ring-1 ring-indigo-500/50' : ''}`}
                    >
                      {member.photo
                        ? <img src={member.photo} className="w-6 h-6 rounded-full object-cover" alt="" />
                        : <div className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white shrink-0" style={{ background: member.color || '#6366f1' }}>{member.name[0]}</div>
                      }
                      <div className="min-w-0 flex-1">
                        <div className="text-[11px] text-slate-200 truncate">{member.name}</div>
                        <div className="text-[9px] text-slate-500 truncate">{member.isArtist ? 'Artiste' : 'Team'}{(member.roles || [member.role]).length > 0 ? ` · ${(member.roles || [member.role]).join(', ')}` : ''}</div>
                      </div>
                      {isSelected && (
                        <svg className="w-4 h-4 text-indigo-400 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                    );
                  })
                )}
              </div>

              {/* Time pickers */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[9px] text-slate-500 uppercase font-bold">Début</label>
                  <input type="time" value={assignStartTime} onChange={e => setAssignStartTime(e.target.value)}
                    className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded px-2 py-1.5 text-xs text-white focus:outline-none focus:border-indigo-500 [color-scheme:dark]" />
                </div>
                <div>
                  <label className="text-[9px] text-slate-500 uppercase font-bold">Fin</label>
                  <input type="time" value={assignEndTime} onChange={e => setAssignEndTime(e.target.value)}
                    className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded px-2 py-1.5 text-xs text-white focus:outline-none focus:border-indigo-500 [color-scheme:dark]" />
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-[#2D3139] bg-[#0A0B0E] flex gap-2 rounded-b-xl">
              <button onClick={() => setAssignModal(null)} className="flex-1 py-2.5 bg-[#1A1D23] hover:bg-[#2D3139] text-slate-300 rounded-lg text-xs font-bold">
                Annuler
              </button>
              <button
                onClick={() => {
                  if (selectedTeamIds.length === 0) return;
                  const slotStart = assignModal.slotStart;
                  const slotEnd = assignModal.slotEnd;
                  // Date LOCALE du créneau : `toISOString()` renvoyait la veille pour un
                  // créneau commençant avant l'offset UTC (ex. 00:30 en UTC+1).
                  const baseDay = dayKey(slotStart);
                  const customStart = assignStartTime !== fmtHour(slotStart) ? new Date(`${baseDay}T${assignStartTime}:00`) : slotStart;
                  let customEnd = assignEndTime !== fmtHour(slotEnd) ? new Date(`${baseDay}T${assignEndTime}:00`) : slotEnd;
                  // Une fin antérieure au début franchit minuit
                  if (customEnd <= customStart) customEnd = new Date(customEnd.getTime() + 86400000);
                  selectedTeamIds.forEach(teamId => {
                    handleAssign(teamId, assignModal.pointId, customStart, customEnd);
                  });
                  setAssignModal(null);
                }}
                disabled={selectedTeamIds.length === 0}
                className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white rounded-lg text-xs font-bold"
              >
                Assigner{selectedTeamIds.length > 0 ? ` (${selectedTeamIds.length})` : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Team Popover */}
      {teamPopover && (
        <TeamPopover
          segment={teamPopover.segment}
          position={{ x: teamPopover.x, y: teamPopover.y }}
          onEdit={(shift) => {
            setTeamPopover(null);
            setEditShiftModal(shift);
          }}
          onDelete={(shiftId) => {
            deleteShift(shiftId);
            setTeamPopover(null);
          }}
          onClose={() => setTeamPopover(null)}
        />
      )}

      {/* Edit Shift Modal */}
      {editShiftModal && (
        <EditShiftModal
          shift={editShiftModal}
          points={allPoints}
          team={allTeam}
          pointsMap={pointsMap}
          onSave={(id, updated) => updateShift(id, updated)}
          onDelete={(id) => deleteShift(id)}
          onClose={() => setEditShiftModal(null)}
        />
      )}

      {/* Multi-slot picker modal */}
      {multiSlotPicker && (
        <MultiSlotPicker
          pointName={multiSlotPicker.pointName}
          existingDates={multiSlotPicker.existingDates}
          presetDate={selectedDay}
          defaultStart={allPoints.find(p => p.id === multiSlotPicker.pointId)?.openingHours?.[0]?.start?.substring(11, 16)}
          defaultEnd={allPoints.find(p => p.id === multiSlotPicker.pointId)?.openingHours?.[0]?.end?.substring(11, 16)}
          onAdd={(slots) => {
            const pt = allPoints.find(p => p.id === multiSlotPicker.pointId);
            if (pt) {
              const updatedHours = [...(pt.openingHours || []), ...slots];
              updatePoint(multiSlotPicker.pointId, { openingHours: updatedHours });
            }
            setMultiSlotPicker(null);
          }}
          onClose={() => setMultiSlotPicker(null)}
        />
      )}

      {/* Overlap warning modal */}
      {overlapWarning && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-[#14161B] border border-[#2D3139] w-full max-w-sm rounded-2xl shadow-2xl p-6">
            <h3 className="text-white font-bold text-sm mb-2 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400" /> Conflit Horaire
            </h3>
            <p className="text-slate-300 text-xs leading-relaxed mb-4">
              <strong>{overlapWarning.teamName}</strong> a déjà un shift qui chevauche ce créneau{' '}
              {overlapWarning.overlapPointName && overlapWarning.overlapPointName !== overlapWarning.pointName ? (
                <>sur <strong>{overlapWarning.overlapPointName}</strong></>
              ) : (
                <>sur <strong>{overlapWarning.pointName}</strong></>
              )}
              {overlapWarning.overlapStart && (
                <><br /><span className="text-slate-500">{overlapWarning.overlapStart} → {overlapWarning.overlapEnd}</span></>
              )}. Forcer l'assignation ?
            </p>
            <div className="flex gap-2">
              <button onClick={() => { setOverlapWarning(null); setPendingAssign(null); }}
                className="flex-1 py-2 bg-[#0A0B0E] hover:bg-[#2D3139] text-slate-300 rounded-lg text-xs font-bold">Annuler</button>
              <button onClick={() => {
                if (pendingAssign) {
                  handleAssign(pendingAssign.teamId, pendingAssign.pointId, pendingAssign.start, pendingAssign.end, true);
                }
              }} className="flex-1 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-xs font-bold">Forcer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AddTeamModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { addTeam, categories, currentUser } = useStore();
  const visibleCats = categories.filter(c => !c.isGeneral);
  const [sName, setSName] = useState('');
  const [sEmail, setSEmail] = useState('');
  const [sPhone, setSPhone] = useState('');
  const [sCat, setSCat] = useState(currentUser.role === 'manager' ? (currentUser.categoryId || '') : '');

  if (!isOpen) return null;

  const handleAdd = () => {
    if (!sName.trim()) return;
    addTeam({
      name: sName.trim(), email: sEmail.trim(), phone: sPhone.trim(),
      categoryId: sCat || undefined, role: '', roles: [], isOnDuty: false, color: '',
    });
    setSName(''); setSEmail(''); setSPhone('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[160] flex items-center justify-center p-4">
      <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl w-full max-w-sm shadow-2xl">
        <div className="p-5 border-b border-[#2D3139] flex items-center justify-between">
          <h2 className="font-bold text-white text-lg flex items-center gap-2"><UserPlus className="w-5 h-5 text-emerald-400" /> Nouveau Team</h2>
          <button onClick={onClose} className="p-2 hover:bg-[#2D3139] rounded-xl text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 block">Nom <span className="text-red-400">*</span></label>
            <input value={sName} onChange={e => setSName(e.target.value)} placeholder="Nom complet" className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-2.5 text-white text-sm placeholder-slate-600 focus:border-indigo-500 focus:outline-none" />
          </div>
          <div>
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 block">Email</label>
            <input value={sEmail} onChange={e => setSEmail(e.target.value)} placeholder="email@exemple.com" className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-2.5 text-white text-sm placeholder-slate-600 focus:border-indigo-500 focus:outline-none" />
          </div>
          <div>
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 block">Telephone</label>
            <input value={sPhone} onChange={e => setSPhone(e.target.value)} placeholder="+216..." className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-2.5 text-white text-sm placeholder-slate-600 focus:border-indigo-500 focus:outline-none" />
          </div>
          {currentUser.role === 'admin' && (
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 block">Categorie</label>
              <select value={sCat} onChange={e => setSCat(e.target.value)} className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-2.5 text-white text-sm focus:border-indigo-500 focus:outline-none">
                <option value="">Sans categorie</option>
                {visibleCats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
        </div>
        <div className="p-5 border-t border-[#2D3139] flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 bg-[#1A1D23] border border-[#2D3139] text-slate-400 rounded-xl text-sm font-bold">Annuler</button>
          <button onClick={handleAdd} disabled={!sName.trim()} className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white rounded-xl text-sm font-bold">Ajouter</button>
        </div>
      </div>
    </div>
  );
}
